'use client';

import { useState, useEffect, Fragment } from 'react';
import { apiClient } from '@/app/api/client';
import { apiConfig } from '@/config/api';
import { articlesApi } from '@/app/api/articles';
import { Dialog, Transition } from '@headlessui/react';
import { Button } from '@/components/forms';
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/solid';
import { blogSpotPublishingService, BlogSpotConnection, PublishingProgress } from '@/services/blogspot-publishing';
import { ContentConverter } from '@/utils/contentConverter';
import { customHttpPublishingService, CustomHttpConnection } from '@/services/custom-http-publishing';
import { mediumPublishingService, MediumConnection } from '@/services/medium-publishing';
import { wordPressPublishingService, WordPressConnection } from '@/services/wordpress-publishing';
import { GlobeAltIcon } from "@heroicons/react/24/outline";

interface Connection {
  id: string;
  platformName: string;
  platformType: string;
  iconUrl: string;
  settings?: any;
}

interface PublishingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: (connectionIds: string[]) => void;
  isPublishing: boolean;
  articleId: string;
  preselectedConnectionId?: string; // For direct publishing from dropdown
}

export default function PublishingModal({ isOpen, onClose, onPublish, isPublishing, articleId, preselectedConnectionId }: PublishingModalProps) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishingProgress, setPublishingProgress] = useState<Record<string, PublishingProgress>>({});
  const [isPublishingToConnections, setIsPublishingToConnections] = useState(false);
  const [allPublishingComplete, setAllPublishingComplete] = useState(false);
  const [isLoggingMode, setIsLoggingMode] = useState(false);
  const [publishingLogs, setPublishingLogs] = useState<Array<{ id: string, type: 'info' | 'success' | 'error', message: string, timestamp: string }>>([]);
  const [isFromDropdown, setIsFromDropdown] = useState(false);
  const [hasStartedPublishing, setHasStartedPublishing] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<{ type: 'title' | 'markdown' | 'html' | 'importUrl', status: 'success' | 'error' } | null>(null);
  const [authorSettings, setAuthorSettings] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setAllPublishingComplete(false);
      setPublishingProgress({});
      setError(null);
      setPublishingLogs([]);
      setHasStartedPublishing(false);
      setIsManualMode(false);

      const fetchConnections = async () => {
        try {
          setLoading(true);
          const [connectionsRes, settingsRes] = await Promise.all([
            apiClient.get('/api/connections'),
            apiClient.get('/api/user/settings')
          ]);
          setConnections(connectionsRes.data || []);
          setAuthorSettings(settingsRes.data || {});

          // Determine mode based on preselected connection AFTER connections are loaded
          if (preselectedConnectionId) {
            if (preselectedConnectionId === 'manual') {
              setIsManualMode(true);
              setIsLoggingMode(false);
            } else {
              setIsManualMode(false);
              setIsLoggingMode(true);
              setSelectedConnection(preselectedConnectionId);
              setIsFromDropdown(true);
            }
          } else {
            setIsManualMode(false);
            setIsLoggingMode(false);
            setSelectedConnection('');
            setIsFromDropdown(false);
          }
        } catch (err) {
          setError('Failed to load publishing destinations.');
        } finally {
          setLoading(false);
        }
      };
      fetchConnections();
    }
  }, [isOpen, preselectedConnectionId]);

  const addLog = (type: 'info' | 'success' | 'error', message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setPublishingLogs(prev => [...prev, { id, type, message, timestamp }]);
  };

  const copyLogsToClipboard = () => {
    const logsText = publishingLogs.map(log =>
      `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    navigator.clipboard.writeText(logsText);
  };

  const handleConnectionSelect = (connectionId: string) => {
    if (connectionId === 'manual') {
      setIsManualMode(true);
      setIsLoggingMode(false);
    } else {
      setSelectedConnection(connectionId);
      setIsLoggingMode(true);
    }
    setIsFromDropdown(false);
  };

  const handlePublish = async () => {
    if (!selectedConnection) return;

    setIsLoggingMode(true);
    setIsFromDropdown(false); // This is from the selection mode, not dropdown
    // Don't start publishing immediately - wait for user to click Publish in logging mode
  };

  const handleStartPublishing = async () => {
    if (!selectedConnection) return;

    setHasStartedPublishing(true);
    await handleDirectPublish(selectedConnection);
  };

  const handleBackToSelection = () => {
    setIsLoggingMode(false);
    setPublishingLogs([]);
    setHasStartedPublishing(false);
    // Keep selectedConnection so it remains highlighted when returning
  };

  const handleDirectPublish = async (connectionId: string) => {
    setIsPublishingToConnections(true);
    setPublishingProgress({});
    setPublishingLogs([]);

    try {
      const connection = connections.find(c => c.id === connectionId);
      if (!connection) {
        addLog('error', 'Connection not found');
        return;
      }

      addLog('info', `Starting publication to ${connection.platformName}...`);

      // Get article content and metadata
      addLog('info', 'Fetching article content and metadata...');
      const [article, content] = await Promise.all([
        articlesApi.getArticle(articleId),
        articlesApi.getLiveContent(articleId)
      ]);

      // Convert content based on connection format setting
      const targetFormat = connection.settings?.format || 'html';
      addLog('info', `Converting content from markdown to ${targetFormat}...`);

      const convertedContent = await ContentConverter.prepareForPublishing(
        content,
        targetFormat,
        {
          title: article.meta.title || 'Untitled',
          tags: article.meta.tags || [],
          excerpt: article.meta.excerpt || undefined
        }
      );

      const post = {
        title: article.meta.title || 'Untitled',
        content: convertedContent,
        tags: article.meta.tags || [],
        excerpt: article.meta.excerpt,
        authorSettings: authorSettings ? {
          jobTitle: authorSettings.jobTitle,
          bio: authorSettings.bio,
          socialLinks: authorSettings.socialLinks
        } : undefined
      };

      let lastExternalUrl: string | undefined = undefined;
      const progressCallback = (progress: any) => {
        setPublishingProgress(prev => ({
          ...prev,
          [progress.connectionId]: progress
        }));
        addLog(progress.status === 'error' ? 'error' : 'info', progress.message);
        if (progress.externalUrl) lastExternalUrl = progress.externalUrl;
      };

      addLog('info', `Publishing to ${connection.platformName}...`);

      console.log('Publishing to ' + connection.platformName, post);

      // Route to appropriate publishing service
      if (connection.platformType === 'blogspot') {
        const blogSpotConn: BlogSpotConnection = {
          id: connection.id,
          settings: connection.settings
        };
        await blogSpotPublishingService.publishToBlogSpot(blogSpotConn, {
          title: post.title,
          content: post.content,
          labels: post.tags,
          summary: article.meta.summary || article.meta.excerpt,
          authorSettings: post.authorSettings
        }, progressCallback);
      } else if (connection.platformType === 'medium') {
        const mediumConn: MediumConnection = {
          id: connection.id,
          settings: connection.settings
        };
        await mediumPublishingService.publishToMedium(mediumConn, post, progressCallback);
      } else if (connection.platformType === 'wordpress') {
        const wordPressConn: WordPressConnection = {
          id: connection.id,
          settings: connection.settings
        };
        await wordPressPublishingService.publishToWordPress(wordPressConn, {
          title: post.title,
          content: post.content,
          tags: post.tags,
          categories: article.meta.category ? [article.meta.category] : [],
          excerpt: post.excerpt,
          status: 'publish',
          authorSettings: post.authorSettings
        }, progressCallback);
      } else if (connection.platformType === 'wordpress_oauth') {
        addLog('info', 'Publishing to WordPress.com via OAuth...');
        const wordPressOAuthConn = {
          id: connection.id,
          settings: connection.settings
        };
        await wordPressPublishingService.publishToWordPressOAuth(wordPressOAuthConn, {
          title: post.title,
          content: post.content,
          tags: post.tags,
          categories: article.meta.category ? [article.meta.category] : [],
          excerpt: post.excerpt,
          status: connection.settings.defaultStatus || 'publish',
          authorSettings: post.authorSettings
        }, progressCallback);
      } else if (connection.platformType === 'custom_http') {
        const customConn: CustomHttpConnection = {
          id: connection.id,
          settings: {
            endpoint_url: connection.settings.endpoint_url,
            http_method: connection.settings.http_method || 'POST',
            headers: connection.settings.headers || {},
            payload_mapping: connection.settings.payload_mapping || {},
            format: connection.settings.format
          }
        };
        await customHttpPublishingService.publishToCustomHttp(customConn, post, progressCallback);
      }

      addLog('success', `Successfully published to ${connection.platformName}!`);
      // Record success in backend with external URL if available
      try {
        await articlesApi.recordPublication(articleId, {
          connectionId: connection.id,
          platformName: connection.platformName,
          status: 'published',
          externalUrl: lastExternalUrl
        });
      } catch (e) {
        // non-blocking
      }
      // Notify parent to refresh publication status if provided via onPublish prop
      if (typeof onPublish === 'function') {
        try { onPublish([connection.id]); } catch { }
      }
      setAllPublishingComplete(true);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addLog('error', `Publishing failed: ${errorMessage}`);
      // Record failure in backend
      try {
        const connection = connections.find(c => c.id === connectionId);
        if (connection) {
          await articlesApi.recordPublication(articleId, {
            connectionId: connection.id,
            platformName: connection.platformName,
            status: 'failed',
            errorMessage: errorMessage
          });
        }
      } catch (e) {
        // non-blocking
      }
      setError(errorMessage);
    } finally {
      setIsPublishingToConnections(false);
    }
  };

  const getSelectedConnectionName = () => {
    if (!selectedConnection) return '';
    const connection = connections.find(c => c.id === selectedConnection);
    return connection?.platformName || '';
  };

  const handleCopyToClipboard = async (text: string) => {
    // A toast hook could be used here to provide feedback
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleCopyImportUrl = async () => {
    try {
      // The articleId is in the format `art_<uuid>`
      const uuid = articleId.startsWith('art_') ? articleId.substring(4) : articleId;
      const importUrl = `${apiConfig.baseUrl}/public/import/${uuid}`;
      await handleCopyToClipboard(importUrl);
      setCopyFeedback({ type: 'importUrl', status: 'success' });
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Failed to copy import URL:', error);
      setCopyFeedback({ type: 'importUrl', status: 'error' });
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };

  const handleManualCopy = async (type: 'title' | 'markdown' | 'html') => {
    try {
      const article = await articlesApi.getArticle(articleId);
      const content = await articlesApi.getLiveContent(articleId);

      let textToCopy = '';
      switch (type) {
        case 'title':
          textToCopy = article.meta.title || 'Untitled';
          break;
        case 'markdown':
          textToCopy = content;
          break;
        case 'html':
          textToCopy = await ContentConverter.prepareForPublishing(content, 'html', {});
          break;
      }

      await handleCopyToClipboard(textToCopy);
      setCopyFeedback({ type, status: 'success' });

      // Clear feedback after 2 seconds
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error(`Failed to copy ${type}:`, error);
      setCopyFeedback({ type, status: 'error' });

      // Clear feedback after 3 seconds for errors
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };


  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10 focus:outline-none" onClose={() => { }}>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
                <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                  {isManualMode
                    ? 'Manual Publish'
                    : isLoggingMode
                      ? hasStartedPublishing
                        ? `Publishing to ${getSelectedConnectionName()}...`
                        : `Publish to ${getSelectedConnectionName()}`
                      : 'Publish Article'}
                </Dialog.Title>

                {!isLoggingMode && !isManualMode && (
                  <p className="mt-2 text-sm text-gray-500">
                    {connections.length > 0 ? 'Click the connection to start publish' : 'No connections available. Manage your connections in your profile.'}
                  </p>
                )}

                <div className="mt-6 space-y-3">
                  {loading && <p className="text-sm text-gray-500">Loading destinations...</p>}
                  {error && <p className="text-sm text-red-600">{error}</p>}

                  {!loading && !error && !isLoggingMode && !isManualMode && (
                    <>
                      {connections.length > 0 ? (
                        <>
                          <div className="flex items-center p-3 border rounded-lg bg-gray-100 text-gray-500">
                            <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                            <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cloudflare.svg" alt="R2" className="w-6 h-6 mx-3" />
                            <span className="font-medium">R2 Storage (Default)</span>
                          </div>
                          {connections.map(conn => (
                            <div
                              key={conn.id}
                              className={`border rounded-lg transition-all cursor-pointer ${selectedConnection === conn.id
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                }`}
                              onClick={() => handleConnectionSelect(conn.id)}
                            >
                              <div className="flex items-center p-3">
                                <img src={conn.iconUrl} alt={conn.platformName} className="w-6 h-6 mr-3" />
                                <div className="flex-1">
                                  <span className="font-medium text-gray-800 select-none">{conn.platformName}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="border-t border-gray-200 my-3"></div>
                          <div
                            className='border rounded-lg transition-all cursor-pointer border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                            onClick={() => handleConnectionSelect('manual')}
                          >
                            <div className="flex items-center p-3">
                              <GlobeAltIcon className="w-6 h-6 mr-3 text-gray-600" />
                              <div className="flex-1">
                                <span className="font-medium text-gray-800 select-none">Manual Publish</span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 mb-2">
                            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-500">No publishing connections configured.</p>
                          <p className="text-xs text-gray-400 mt-1">Add connections in your profile to publish to external platforms.</p>
                        </div>
                      )}
                    </>
                  )}

                  {isManualMode && (
                    <div className="space-y-4 pt-2">
                      <p className="text-sm text-gray-600">Copy the article details to manually post on a platform that isn't connected.</p>

                      <div className="space-y-3">
                        <h4 className="text-md font-semibold text-gray-800">Import URL</h4>
                        <p className="text-xs text-gray-500 mb-2">For platforms that support importing from a URL, like Medium.</p>
                        <div className="flex items-center gap-2">
                          <Button plain onClick={handleCopyImportUrl} className="flex-1">
                            Copy Import URL
                          </Button>
                          {copyFeedback?.type === 'importUrl' && (
                            <span className={`text-xs ${copyFeedback.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                              {copyFeedback.status === 'success' ? '✓ Copied!' : '✗ Failed'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <h4 className="text-md font-semibold text-gray-800">Title</h4>
                        <p className="text-xs text-gray-500">Copies the article's main title.</p>
                        <div className="flex items-center gap-2">
                          <Button plain onClick={() => handleManualCopy('title')} className="flex-1">
                            Copy Title
                          </Button>
                          {copyFeedback?.type === 'title' && (
                            <span className={`text-xs ${copyFeedback.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                              {copyFeedback.status === 'success' ? '✓ Copied!' : '✗ Failed'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <h4 className="text-md font-semibold text-gray-800">Content</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-2">For platforms that support raw Markdown, like Dev.to or Hashnode.</p>
                            <div className="flex items-center gap-2">
                              <Button plain onClick={() => handleManualCopy('markdown')} className="flex-1">
                                Copy as Markdown
                              </Button>
                              {copyFeedback?.type === 'markdown' && (
                                <span className={`text-xs ${copyFeedback.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                  {copyFeedback.status === 'success' ? '✓ Copied!' : '✗ Failed'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500 mb-2">For platforms with a rich text editor, like WordPress or a custom CMS.</p>
                            <div className="flex items-center gap-2">
                              <Button plain onClick={() => handleManualCopy('html')} className="flex-1">
                                Copy as HTML
                              </Button>
                              {copyFeedback?.type === 'html' && (
                                <span className={`text-xs ${copyFeedback.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                  {copyFeedback.status === 'success' ? '✓ Copied!' : '✗ Failed'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isLoggingMode && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          {hasStartedPublishing ? 'Publishing Logs' : 'Ready to Publish'}
                        </span>
                        {hasStartedPublishing && (
                          <button
                            onClick={copyLogsToClipboard}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy Logs
                          </button>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs">
                        {!hasStartedPublishing && (
                          <div className="text-gray-500">Click "Publish" to start publishing to {getSelectedConnectionName()}...</div>
                        )}
                        {hasStartedPublishing && publishingLogs.length === 0 && (
                          <div className="text-gray-500">Initializing...</div>
                        )}
                        {publishingLogs.map(log => (
                          <div key={log.id} className={`flex items-start gap-2 mb-1 ${log.type === 'error' ? 'text-red-600' :
                            log.type === 'success' ? 'text-green-600' :
                              'text-gray-700'
                            }`}>
                            <span className="text-gray-400 shrink-0">[{log.timestamp}]</span>
                            <div className="flex items-center gap-1">
                              {log.type === 'error' && <ExclamationTriangleIcon className="h-3 w-3 text-red-500" />}
                              {log.type === 'success' && <CheckCircleIcon className="h-3 w-3 text-green-500" />}
                              {log.type === 'info' && <ClockIcon className="h-3 w-3 text-blue-500" />}
                              <span>{log.message}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex justify-between items-center">
                  <div>
                    {isLoggingMode && !isFromDropdown && !hasStartedPublishing && (
                      <Button plain onClick={handleBackToSelection}>
                        Back
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-4">
                    {!isLoggingMode && !isManualMode && (
                      <Button plain onClick={() => setIsManualMode(true)}>
                        Publish Manually
                      </Button>
                    )}
                    <Button plain onClick={onClose}>
                      {isManualMode || allPublishingComplete ? 'Done' : 'Cancel'}
                    </Button>
                    {isLoggingMode && !hasStartedPublishing && (
                      <Button onClick={handleStartPublishing} disabled={isPublishingToConnections}>
                        {isPublishingToConnections ? 'Publishing...' : 'Publish'}
                      </Button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
